import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useMemo } from 'react';

import { cn } from '@growtherapy/sprout-ui';

import { Navbar, PageContainer, PageProviders, PushDrawerContainer, StageContainer } from '../../components';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { nonEssentialElementProps, useSessionFocus } from '../../hooks';
import { isMediaShareModeAtom, useHandleDeviceChanges } from '../../session';
import { LeaveSessionConfirmationModal } from '../../session/components/LeaveSessionConfirmationModal';
import { useNavigationGuard } from '../../session/hooks/useNavigationGuard';
import { usePeriodicallySendParticipantState } from '../../session/messages/usePeriodicallySendParticipantState';
import { SessionGrid } from '../../session/session-grid/SessionGrid';
import { useNetworkQualityLogging } from '../../session/useNetworkQualityLogging';
import { useResetLocalVideoOnScreenOrientationChange } from '../../session/useResetLocalVideoOnScreenOrientationChange';
import { useResetVideoOnMobileNavigation } from '../../session/useResetVideoOnMobileNavigation';
import { Theme } from '../../state';
import { useUpdateTrackStatsInterval } from '../../video-sdk/twilio';
import { HelpModalWrapper as HelpModal } from '../HelpModal';
import { PushDrawerWrapper as PushDrawer } from '../PushDrawer';
import { RemoteParticipant } from '../RemoteParticipant';
import { SelfVideoThumbnail } from '../SelfVideoThumbnail';
import { useObscuredVisitorContentProps } from '../a11y';
import { MEETING_ROOM_PUSH_DRAWER_ARIA_CONTROLS_ID } from '../constants';
import { useSendVisitorParticipantStateCallback } from '../messages/useSendVisitorParticipantState';
import { TranscriptionPillWrapper } from '../session-recording/TranscriptionPill';
import { VisitorDrawerState, visitorDrawerStateAtom, visitorIsHelpModalOpenAtom } from '../state';
import { VisitorWhiteboard } from './VisitorWhiteboard';
import { VisitorMeetingSessionControlsWrapper as SessionControls } from './controls/VisitorMeetingSessionControls';
import { VisitorRejoinModalWrapper } from './rejoin-session/VisitorRejoinModalWrapper';
import { useClientEnterSessionRoomLogger } from './useClientEnterSessionRoomLogger';
import { useClientRoomEventListeners } from './useClientRoomEventListeners';
import { useLeaveSessionNudgeToast } from './useLeaveSessionNudgeToast';
import { useSessionConversation } from './useSessionConversation';
import { useSetProviderParticipantData } from './useSetProviderParticipantData';

function StageEffects() {
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const sendParticipantState = useSendVisitorParticipantStateCallback();
  useClientRoomEventListeners();
  useHandleDeviceChanges();
  useNetworkQualityLogging({
    onOpenSettings: () => {
      setDrawerState(VisitorDrawerState.SETTINGS);
    },
  });
  useSessionFocus();
  useResetLocalVideoOnScreenOrientationChange();
  useResetVideoOnMobileNavigation();
  useLeaveSessionNudgeToast();
  useSetProviderParticipantData();
  useUpdateTrackStatsInterval();
  usePeriodicallySendParticipantState(sendParticipantState);

  return null;
}

const Stage = memo(function Stage() {
  const isMediaShareMode = useAtomValue(isMediaShareModeAtom);
  const obscuredContentProps = useObscuredVisitorContentProps();

  const thumbnail = useMemo(
    () => (
      <SelfVideoThumbnail
        showExpandVideoToggle={false}
        shouldShowHideSelf={false}
        showSetPositionMenu={false}
        videoClassName={cn({
          'max-w-[15rem] max-h-[8.5rem] min-w-[unset] w-auto h-full [&>video]:relative mb-2 flex-1 rounded-2xl border-neutral-800 border rebrand:border-border-state-selected-emphasis':
            isMediaShareMode,
        })}
        className={cn(
          'max-w-[15rem] max-h-[8.5rem] min-w-[unset] w-auto h-full [&>video]:relative mr-2 sm:mr-0 flex-1 border border-neutral-800 rounded-2xl rebrand:border-border-state-selected-emphasis',
          {
            'flex-1 min-w-[10rem] sm:min-w-[unset] min-h-[7.875rem] w-auto h-full sm:mb-2': isMediaShareMode,
          },
        )}
      />
    ),
    [isMediaShareMode],
  );

  return (
    <>
      <StageEffects />
      <StageContainer {...obscuredContentProps} rounded>
        <SessionGrid
          whiteboardComponent={VisitorWhiteboard}
          remoteParticipantComponent={RemoteParticipant}
          thumbnail={thumbnail}
        />
        {!isMediaShareMode && (
          <SelfVideoThumbnail
            className='absolute bottom-2'
            showSetPositionMenu
            shouldShowHideSelf
            showExpandVideoToggle={false}
          />
        )}
      </StageContainer>
    </>
  );
});

export function Page() {
  useClientEnterSessionRoomLogger();
  useSessionConversation();
  const obscuredContentProps = useObscuredVisitorContentProps();
  const [isHelpModalOpen, setIsHelpModalOpen] = useAtom(visitorIsHelpModalOpenAtom);

  const { isBlocked, stay, leave } = useNavigationGuard(true);

  const openHelpModal = useCallback(() => {
    sendLoggingEvents(TrackingEvents.ANY_HELP_REQUEST);
    sendLoggingEvents(TrackingEvents.CLIENT_HELP_REQUEST);
    setIsHelpModalOpen(true);
  }, [setIsHelpModalOpen]);

  return (
    <PageProviders theme={Theme.DARK}>
      <LeaveSessionConfirmationModal isOpen={isBlocked} onStay={stay} onLeave={leave} />
      <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
      <VisitorRejoinModalWrapper />
      <PageContainer>
        <Navbar
          {...obscuredContentProps}
          onClickNeedHelpButton={openHelpModal}
          TranscriptionPillComponent={TranscriptionPillWrapper}
          showLanguageSwitcher
          {...nonEssentialElementProps}
          className={cn(nonEssentialElementProps.className, 'order-1')}
        />
        <SessionControls />
        <PushDrawerContainer
          // The push drawer must come _after_ the buttons that can trigger it
          // in the DOM. This is so that a keyboard user can tab forward from
          // the triggering button to the drawer. However, the drawer and stage
          // are meant to be visually positioned _before_ the control buttons,
          // so we reorder them within their flex container.
          className='order-2 sm:px-2'
        >
          <Stage />
          <PushDrawer id={MEETING_ROOM_PUSH_DRAWER_ARIA_CONTROLS_ID} />
        </PushDrawerContainer>
      </PageContainer>
    </PageProviders>
  );
}
